/**
 * Status tracking scope values
 */
export enum StatusTrackingScopeEnum {
  /**
   * Represents a tracked status value that is imposed on the specific record.
   * @description
   * This scope indicates that the status is directly related to the record itself and it may be a result of
   * a user action or system process that affects the record's state.
   * @example
   * For example, a vehicle's status may be "Out of Service", which directly reflects the vehicle's condition
   * and affects its availability for use.
   * The status was applied to the record itself, and it is not a result of any other record's status.
   */
  RECORD = 'record',

  /**
   * Represents a tracked status value of a linked record that does not directly affect the current record.
   * @description
   * This scope indicates that the status is related to another record that is linked to the current record,
   * but it does not directly affect the current record's state.
   * @example
   * For example, a vehicle insurance record may have a status of "Expired", which indicates that the insurance is no longer valid.
   * This status is related to the vehicle's insurance record, but it does not directly affect the vehicle's condition or availability.
   * The status was applied to the linked record, but is relevant to the current record as it helps to
   * understand the vehicle's overall state in the context of its legal and operational requirements.
   */
  LINK = 'link',

  /**
   * Represents a tracked status value that is imposed on the specific record for the purposes of availability tracking.
   * @description
   * This scope indicates that the status is directly related to the record itself and it may be a result of
   * a user action or system process that affects the record's state specifically in terms of its availability for use.
   * This is particularly useful for scenarios where the record's availability needs to be tracked separately from its general status.
   * @example
   * For example, a vehicle's status in "record" scope is "rented" due to an active rental agreement. However, the agreement has lapsed,
   * but the vehicle still has not been returned by the customer. Thus, the vehicle is not in our possession and its status is considered "rented"
   * until it is returned (ie. the status is open ended and doesn't have an "effectiveTo" date set).
   * In practice this represents real-world scenarios where the record's availability cannot be determined solely by related agreements' dates,
   * but at the same time it would make it impossible to plan future use of the record if it was not tracked separately.
   * Hence, we use this scope in combination with the "record" scope to track the record's availability status.
   */
  RECORD_AVAILABILITY = 'record_availability',
}
